/**
 * SVD Ad Report — the agency's weekly Google Doc, shown in full.
 *
 * The server fetches the doc, stores the pictures privately and serves a
 * cleaned copy at /api/ad-report/view. It is framed here WITHOUT allow-scripts,
 * so nothing inside an agency-editable document can ever run.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, MenuItem, Select, Stack, Typography } from '@mui/material';
import { OpenInNew as OpenInNewIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { APP_BAR_HEIGHT } from '../../theme/layoutConstants';
import { DOC_URL, fetchMeta, ReportMeta, requestRefresh, VIEW_URL } from './api';

const DEFAULT_DOC = '1ovEKsLyph0Qg7P0RjaLp-yubsLCF0HGNP2M3Be4S8WM';

const AdReportPage: React.FC = () => {
  const [meta, setMeta] = useState<ReportMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [week, setWeek] = useState('');
  const [frameKey, setFrameKey] = useState(0);
  const [src, setSrc] = useState(VIEW_URL);
  const wasReady = useRef(false);

  const load = useCallback(() => {
    fetchMeta().then((m) => { setMeta(m); setError(null); }).catch((e: Error) => setError(e.message));
  }, []);

  useEffect(() => { load(); }, [load]);

  // First ever load builds the copy (a minute or two); poll until it is ready.
  useEffect(() => {
    if (meta && meta.ready && !meta.refreshing) return undefined;
    const t = window.setInterval(load, 4000);
    return () => window.clearInterval(t);
  }, [meta, load]);

  useEffect(() => {
    if (meta?.ready && !wasReady.current) { wasReady.current = true; setFrameKey((k) => k + 1); }
  }, [meta]);

  const jump = (id: string) => {
    setWeek(id);
    setSrc(id ? `${VIEW_URL}#${id}` : VIEW_URL);
    setFrameKey((k) => k + 1);
  };

  const refresh = () => {
    requestRefresh().catch(() => undefined).finally(() => { setTimeout(load, 1500); });
  };

  const updated = meta?.fetchedAt ? new Date(meta.fetchedAt).toLocaleString() : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: `calc(100vh - ${APP_BAR_HEIGHT}px)` }}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ flexShrink: 0, px: 2, py: 0.75, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', flexWrap: 'wrap' }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>SVD Ad Report</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {meta?.refreshing ? 'Refreshing from Google Docs…' : updated ? `Updated ${updated}${meta?.stale ? ' (Google unreachable, showing last copy)' : ''}` : ''}
        </Typography>
        {meta && meta.weeks.length > 0 && (
          <Select size="small" displayEmpty value={week} onChange={(e) => jump(String(e.target.value))} sx={{ minWidth: 220 }}>
            <MenuItem value="">Jump to week…</MenuItem>
            {meta.weeks.map((w) => <MenuItem key={w.id} value={w.id}>{w.label}</MenuItem>)}
          </Select>
        )}
        <Button size="small" startIcon={<RefreshIcon />} onClick={refresh} disabled={!!meta?.refreshing}>Refresh</Button>
        <Button
          size="small"
          endIcon={<OpenInNewIcon />}
          href={DOC_URL(meta?.docId || DEFAULT_DOC)}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open in Google Docs
        </Button>
      </Stack>
      {error && <Alert severity="error" square sx={{ borderRadius: 0 }}>Could not load the report: {error}</Alert>}
      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {meta && !meta.ready ? (
          <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ height: '100%' }}>
            <CircularProgress />
            <Typography color="text.secondary">
              Preparing the report for the first time. This takes a minute or two, then it is instant.
            </Typography>
            {meta.lastError && <Alert severity="warning">{meta.lastError}</Alert>}
          </Stack>
        ) : (
          <iframe
            key={frameKey}
            title="SVD Ad Report"
            src={src}
            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            style={{ border: 0, width: '100%', height: '100%', display: 'block', background: '#fff' }}
          />
        )}
      </Box>
    </Box>
  );
};

export default AdReportPage;
